import React, { Fragment } from 'react';
import { Mutation, Query } from '@apollo/client/react/components';
import { useFeatureFlags, FeatureValue } from '@bench/ff-client';
import { SplitFeatureFlags } from 'bk-web-app/features';

import { SpinnerOverlay } from 'spinner';

import { LedgerQuery, ReviewMutation, ReviewSectionsQuery } from './queries';
import ReviewForm from './ReviewForm';
import KeepAliveContainer from './KeepAliveContainer';
import { InternalRefetchQueryDescriptor } from '@apollo/client';

type ReviewContainerProps = {
  clientId: string;
  startDate: string;
  endDate: string;
  restoreLastChecks: boolean;
  refetchQueries?: InternalRefetchQueryDescriptor[];
  onCloseReview: () => void;
};

const ReviewContainer = ({
  clientId,
  startDate,
  endDate,
  restoreLastChecks,
  refetchQueries,
  onCloseReview,
}: ReviewContainerProps) => {
  const [featureState] = useFeatureFlags();
  const {
    featureContext: { features },
  } = featureState;

  const isFixIncorrectBooksCheckboxEnabled =
    features[SplitFeatureFlags.FIX_INCORRECT_BOOKS_CHECKBOX]?.value === FeatureValue.FEATURE_ON;

  return (
    <Query query={ReviewSectionsQuery}>
      {({ loading: loadingSections, error: errorSections, data: reviewSectionData }: any) => (
        <Query query={LedgerQuery} variables={{ clientId }}>
          {({ loading: loadingLedgerAccounts, error: errorLedgerAccounts }) => {
            if (loadingSections || loadingLedgerAccounts) {
              return <SpinnerOverlay />;
            }

            if (errorSections) {
              return <>Error! ${errorSections.message}</>;
            }

            if (errorLedgerAccounts) {
              return <>Error! ${errorLedgerAccounts.message}</>;
            }

            const reviewSections = reviewSectionData?.reviewSections;

            return (
              <Mutation
                mutation={ReviewMutation}
                refetchQueries={refetchQueries}
                onCompleted={onCloseReview}
              >
                {(submitReview, { loading: loadingMutation, error: errorMutation }) => {
                  if (loadingMutation) {
                    return <SpinnerOverlay />;
                  }

                  if (errorMutation) {
                    return <>Error! ${errorMutation.message}</>;
                  }

                  const buildRequestAndSubmit = (
                    notes,
                    checks,
                    type,
                    requireFixes,
                    requireCategorizationFixes,
                    requireAccountBalanceFixes,
                    requireIncorrectBooksFixes,
                  ) => {
                    submitReview({
                      variables: {
                        review: {
                          clientId,
                          fromMonth: startDate,
                          toMonth: endDate,
                          notes,
                          checks,
                          type,
                          requireFixes,
                          requireCategorizationFixes,
                          requireAccountBalanceFixes,
                          requireIncorrectBooksFixes,
                        },
                      },
                    });
                  };

                  return (
                    <Fragment>
                      <KeepAliveContainer />
                      <ReviewForm
                        reviewSections={reviewSections}
                        clientId={clientId}
                        startDate={startDate}
                        endDate={endDate}
                        restoreLastChecks={restoreLastChecks}
                        onCloseReview={onCloseReview}
                        submitReview={buildRequestAndSubmit}
                        isFixIncorrectBooksCheckboxEnabled={isFixIncorrectBooksCheckboxEnabled}
                      />
                    </Fragment>
                  );
                }}
              </Mutation>
            );
          }}
        </Query>
      )}
    </Query>
  );
};

ReviewContainer.defaultProps = {
  refetchQueries: [],
};

export default ReviewContainer;
